import _ from 'lodash';
import moment from 'moment';
import { ThunkDispatch } from 'redux-thunk';
import I18n from '../../../config/i18n';
import { getObjPath, createObjPath } from '../../../utils/helpers';
import {
  FETCH_PRICES_REQUEST,
  FETCH_PRICES_SUCCESS,
  FETCH_PRICES_FAILURE,
  INVALIDATE_PRICES,
  ActionFetchPricesSuccess,
  ActionFetchPricesFailure,
  ReceivePriceResultLegacy,
  ActionInvalidatePrices,
} from './types';
import type { ActionFetchPricesRequest } from './types';
import { arrivalUndefinedAsString, fetchPrices as fetchPricesApi } from '../priceApi';
import { PricesState } from '../../../routes/reducers/types';
import { PricesAccommodationMap } from '../types';

const arrivalDateFormat = 'DD.MM.YYYY';
let accIdQueue: any = {};
let isQueueing = false;
let invalidateAccIdQueue: number[] = [];
let isInvalidateQueueing = false;

function requestPrices(accIds: number[]): ActionFetchPricesRequest {
  return {
    type: FETCH_PRICES_REQUEST,
    loading: accIds,
    accIds,
  };
}

export function receivePrices(
  priceData: PricesAccommodationMap,
  accIds: number[],
): ActionFetchPricesSuccess {
  const prices: ReceivePriceResultLegacy = accIds.reduce((pricesAcc, accId) => {
    const suggestions = priceData[accId];
    const matchingSuggestion = suggestions && suggestions.matchingSuggestion;
    const alternativeSuggestions = (suggestions && suggestions.alternativeSuggestions) || [];

    return {
      ...pricesAcc,
      [accId]: {
        matchingSuggestion: matchingSuggestion && {
          ...matchingSuggestion,
          arrival: moment(matchingSuggestion.arrival),
          departure: moment(matchingSuggestion.departure),
        },
        alternativeSuggestions:
          alternativeSuggestions &&
          alternativeSuggestions.map((altSugg) => ({
            ...altSugg,
            arrival: moment(altSugg.arrival),
            departure: moment(altSugg.departure),
          })),
      },
    };
  }, {});

  return {
    type: FETCH_PRICES_SUCCESS,
    loading: accIds,
    accIds,
    prices,
  };
}

function failPrices(accIds: number[]): ActionFetchPricesFailure {
  return {
    type: FETCH_PRICES_FAILURE,
    loading: accIds,
    accIds,
  };
}

function fetchPrices(accIds: number[], arrival?: string, durationOfStay?: number) {
  return (
    dispatch: ThunkDispatch<
      PricesState,
      unknown,
      ActionFetchPricesRequest | ActionFetchPricesSuccess | ActionFetchPricesFailure
    >,
  ): void => {
    const lang = I18n.locale;

    dispatch(requestPrices(accIds));

    fetchPricesApi(lang, accIds, false, false, arrival, durationOfStay)
      .then((res) => dispatch(receivePrices(res, accIds)))
      .catch(() => dispatch(failPrices(accIds)));
  };
}

function processQueue() {
  return (dispatch: ThunkDispatch<PricesState, unknown, ActionFetchPricesRequest>): void => {
    getObjPath(accIdQueue).forEach(([path, accIds]): void => {
      const [arrival, durationOfStay] = path;
      dispatch(
        fetchPrices(
          accIds,
          arrival !== arrivalUndefinedAsString ? arrival : undefined,
          durationOfStay !== arrivalUndefinedAsString && !Number.isNaN(durationOfStay)
            ? durationOfStay
            : undefined,
        ),
      );
    });
    accIdQueue = {}; // Reset queue
    isQueueing = false;
  };
}

export function fetchPrice(
  accId: number,
  arrival: string | undefined,
  durationOfStay?: number | null,
) {
  return (dispatch: ThunkDispatch<PricesState, unknown, ActionFetchPricesRequest>): void => {
    const arrivalKey =
      arrival != null ? moment(arrival).format(arrivalDateFormat) : arrivalUndefinedAsString;
    const durationOfStayKey = durationOfStay != null ? durationOfStay : arrivalUndefinedAsString;
    createObjPath(accIdQueue, [], [arrivalKey, durationOfStayKey]);
    const accIds = accIdQueue[arrivalKey][durationOfStayKey];
    accIds.push(accId);

    if (!isQueueing) {
      isQueueing = true;
      _.defer(() => dispatch(processQueue()));
    }
  };
}

function processInvalidateQueue() {
  return (dispatch: ThunkDispatch<PricesState, unknown, ActionInvalidatePrices>): void => {
    dispatch({
      type: INVALIDATE_PRICES,
      accIds: invalidateAccIdQueue,
    });
    isInvalidateQueueing = false;
    invalidateAccIdQueue = [];
  };
}

export function invalidatePrice(accId: number) {
  return (dispatch: ThunkDispatch<PricesState, unknown, ActionInvalidatePrices>): void => {
    invalidateAccIdQueue.push(accId);

    if (!isInvalidateQueueing) {
      isInvalidateQueueing = true;
      _.defer(() => dispatch(processInvalidateQueue()));
    }
  };
}

export default { fetchPrice, receivePrices, invalidatePrice };
